import { cn } from "../lib/cn.js";
import type { InstrumentMatch } from "../lib/useInstrumentSearch.js";
import { PopoverPanel } from "./ui/Popover.js";

/**
 * The suggestion list, split out so `SymbolSearch` is only its state machine.
 *
 * Rows are buttons rather than `<option>`s because the panel is a popover, and
 * the highlight is driven from the input's arrow keys rather than from focus:
 * the reader is typing, and moving focus into the list swallows the next
 * keystroke.
 */
export interface SymbolSearchResultsProps {
  matches: readonly InstrumentMatch[];
  activeIndex: number;
  onHover(index: number): void;
  onPick(symbol: string): void;
  searching: boolean;
}

export function SymbolSearchResults({
  matches,
  activeIndex,
  onHover,
  onPick,
  searching,
}: SymbolSearchResultsProps): React.JSX.Element {
  return (
    <PopoverPanel autoFocus={false} className="w-72">
      <ul id="symbol-search-list">
        {matches.map((match, index) => (
          <li key={match.symbol}>
            <button
              type="button"
              // `onMouseDown` rather than `onClick`: the input blurs first
              // otherwise, the popover closes, and the click lands on nothing.
              onMouseDown={(event) => {
                event.preventDefault();
                onPick(match.symbol);
              }}
              onMouseEnter={() => onHover(index)}
              className={cn(
                "flex w-full items-baseline gap-2 px-2 py-1 text-left text-sm",
                index === activeIndex ? "bg-surface text-text" : "text-text-muted",
              )}
            >
              <span className="font-medium text-text">{match.symbol}</span>
              <span className="truncate text-xs">{match.name}</span>
            </button>
          </li>
        ))}
      </ul>
      {/* Only while a list is already showing, so the panel never opens on a
          spinner alone. */}
      {searching ? <p className="px-2 py-1 text-xs text-text-muted">Searching…</p> : null}
    </PopoverPanel>
  );
}
